import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import MapPanel from "../components/MapPanel";
import LiveMonitor from "../components/LiveMonitor";
import NewsSentiment from "../components/NewsSentiment";
import IssueTrending from "../components/IssueTrending";
import KamtibmasCard from "../components/KamtibmasCard";
import MainIndicators from "../components/MainIndicators";
import RisikoWilayah from "../components/RisikoWilayah";
import HargaPangan from "../components/HargaPangan";
import AnalisisRekomendasiModal from "../components/AnalisisRekomendasiModal";
import IssueDetailModal from "../components/IssueDetailModal";
import MainIndicatorsModal from "../components/MainIndicatorsModal";
import KamtibmasModal from "../components/KamtibmasModal";
import HargaPanganModal from "../components/HargaPanganModal";
import RisikoWilayahModal from "../components/RisikoWilayahModal";
import SentimentAnalysisModal from "../components/SentimentAnalysisModal";
import MediaInsightPanel from "../components/MediaInsightPanel";
import RegionSummaryModal from "../components/RegionSummaryModal";
import AIChatPanel from "../components/AIChatPanel";
import { fetchDashboard, fetchAllIssues, apiClient } from "../lib/api";
import { formatIDR, formatPct, titleCase } from "../lib/format";
import { exportDashboardPDF } from "../lib/pdfExport";

const buildTickerItems = (d) => {
  if (!d) return [];
  const items = [];
  const top = (d.riskData?.topProvinces || [])[0];
  if (top) {
    items.push({
      label: top.status?.toUpperCase() || "RISIKO",
      value: `${titleCase(top.name)} (${top.score}%)`,
      dir: "warn",
    });
  }
  if (d.indicators?.kurs) {
    items.push({
      label: "USD/IDR",
      value: `Rp ${formatIDR(d.indicators.kurs.value)}`,
      change: formatPct(d.indicators.kurs.gap_percentage || 0),
      dir: (d.indicators.kurs.gap_percentage || 0) > 0 ? "up" : "down",
    });
  }
  if (d.indicators?.brent) {
    items.push({
      label: "BRENT OIL",
      value: `$${(d.indicators.brent.value ?? 0).toFixed(2)}`,
      change: formatPct(d.indicators.brent.gap_percentage || 0),
      dir: (d.indicators.brent.gap_percentage || 0) > 0 ? "up" : "down",
    });
  }
  (d.trendingIssues || []).slice(0, 6).forEach((it) => {
    items.push({
      label: "ISU",
      value: titleCase(it.title).slice(0, 60),
      dir: "warn",
    });
  });
  return items;
};

export default function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // 'analisis' | 'issue' | 'indicators' | 'kamtibmas' | 'pangan' | 'risiko' | 'sentiment' | 'media-insight' | 'region' | null
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const mainRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [d, allIssues] = await Promise.all([
          fetchDashboard(),
          fetchAllIssues().catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        if (d?.success && d.data) setDashboard(d.data);
        setIssues(allIssues?.data || d?.data?.trendingIssues || []);
      } catch (e) {
        if (mounted) setError(e?.message || "Failed to load data");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 90000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setModal(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading && !dashboard) {
    return (
      <div className="nms-loading" data-testid="loading-screen">
        <div className="nms-loading-ring" />
        <div className="nms-loading-text">INITIALIZING SYSTEM</div>
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="nms-error" data-testid="error-screen">
        <div className="nms-error-title">UPSTREAM UNAVAILABLE</div>
        <div className="nms-error-msg">{error}</div>
      </div>
    );
  }

  const d = dashboard || {};
  const tickerItems = buildTickerItems(d);
  const close = () => setModal(null);

  if (modal === "media-insight") {
    return <MediaInsightPanel onBack={close} />;
  }

  const dashboardSnapshot = {
    nationalScore: d.riskData?.national,
    nationalStatus: d.riskData?.nationalStatus,
    waspadaCount: (d.riskData?.heatmapData || []).filter((p) => (p.status || "").toLowerCase() === "waspada").length,
    kritisCount: (d.riskData?.heatmapData || []).filter((p) => (p.status || "").toLowerCase() === "kritis").length,
    indicators: d.indicators,
    sentiment: d.sentimentData,
    topIssues: (d.trendingIssues || []).slice(0, 6).map((i) => ({
      title: i.title, percentage: i.percentage, province: i.province,
    })),
    topRiskProvinces: (d.riskData?.topProvinces || []).slice(0, 8),
    topCommodities: (d.commodities || []).slice(0, 8).map((c) => ({
      name: c.name, price: c.price, changePct: c.changePct ?? c.deviation,
    })),
  };

  const doExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      let alertsList = [];
      try {
        const ar = await apiClient.get("/alerts");
        alertsList = (ar.data?.alerts || []).slice(0, 5);
      } catch {/* noop */}

      // Sentiment negative percentage
      const sd = d.sentimentData || {};
      const sentTotal = (sd.positive || 0) + (sd.neutral || 0) + (sd.negative || 0);
      const negPct = sentTotal > 0 ? Math.round(((sd.negative || 0) * 100) / sentTotal) : 0;

      await exportDashboardPDF(null, {
        totalIssues: (d.trendingIssues || []).length || issues.length,
        alerts: alertsList,
        region: "NASIONAL · 38 PROVINSI",
        waspadaCount: dashboardSnapshot.waspadaCount,
        kritisCount: dashboardSnapshot.kritisCount,
        nationalScore: dashboardSnapshot.nationalScore,
        nationalStatus: dashboardSnapshot.nationalStatus,
        topRiskProvinces: dashboardSnapshot.topRiskProvinces,
        negSentimentPct: negPct,
        generatedAt: new Date(),
      });
    } catch (e) {
      console.error("PDF export failed", e);
      // eslint-disable-next-line no-alert
      alert("Export PDF gagal: " + (e.message || ""));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="nms-app" data-testid="dashboard">
      <Header
        tickerItems={tickerItems}
        onOpenAI={() => setAiOpen(true)}
        onExportPDF={doExport}
        exporting={exporting}
      />
      <main className="nms-main" ref={mainRef}>
        <section className="nms-left">
          <div className="nms-map-area">
            <MapPanel
              heatmap={d.riskData?.heatmapData}
              national={d.riskData?.national}
              nationalStatus={d.riskData?.nationalStatus}
              onAnalisisClick={() => setModal("analisis")}
              onProvinceClick={(reg) => { setSelectedRegion(reg); setModal("region"); }}
            >
              <LiveMonitor heatmap={d.riskData?.heatmapData} />
            </MapPanel>
          </div>
          <div className="nms-bottom-row">
            <button type="button" className="card-link" onClick={() => setModal("indicators")} data-testid="open-indicators-modal">
              <MainIndicators indicators={d.indicators} />
            </button>
            <button type="button" className="card-link" onClick={() => setModal("risiko")} data-testid="open-risiko-modal">
              <RisikoWilayah topProvinces={d.riskData?.topProvinces} nationalStatus={d.riskData?.nationalStatus} />
            </button>
            <button type="button" className="card-link" onClick={() => setModal("pangan")} data-testid="open-pangan-modal">
              <HargaPangan commodities={d.commodities} />
            </button>
          </div>
        </section>
        <aside className="nms-right">
          <button type="button" className="card-link" onClick={() => setModal("sentiment")} data-testid="open-sentiment-modal">
            <NewsSentiment data={d.sentimentData} />
          </button>
          <button type="button" className="card-link" onClick={() => setModal("issue")} data-testid="open-trending-modal">
            <IssueTrending issues={issues} />
          </button>
          <button type="button" className="card-link" onClick={() => setModal("kamtibmas")} data-testid="open-kamtibmas-modal">
            <KamtibmasCard data={d.kamtibnasData} />
          </button>
        </aside>
      </main>

      <AnalisisRekomendasiModal open={modal === "analisis"} onClose={close} />
      <IssueDetailModal open={modal === "issue"} onClose={close} onOpenInsight={() => setModal("media-insight")} />
      <MainIndicatorsModal open={modal === "indicators"} onClose={close} />
      <KamtibmasModal open={modal === "kamtibmas"} onClose={close} />
      <HargaPanganModal open={modal === "pangan"} onClose={close} />
      <RisikoWilayahModal open={modal === "risiko"} onClose={close} />
      <SentimentAnalysisModal open={modal === "sentiment"} onClose={close} />
      <RegionSummaryModal open={modal === "region"} region={selectedRegion} onClose={close} />
      <AIChatPanel open={aiOpen} onClose={() => setAiOpen(false)} dashboardSnapshot={dashboardSnapshot} />
    </div>
  );
}
